import { z } from 'zod';

/**
 * Effective access (spec §4.3–4.4, D10). Computed by the API's engine for the signed-in person
 * (or the person being viewed as) and returned by GET /auth/session; the per-person "what they
 * can see, and why" screen (GET /people/:id/access) is the same computation grouped by section.
 */

export const AccessScopeKindSchema = z.enum(['site', 'region', 'division', 'company']);
export type AccessScopeKind = z.infer<typeof AccessScopeKindSchema>;

/** A site, region, division or the whole company, labelled. `id` is null for company. */
export const ScopeRefSchema = z.object({
  kind: AccessScopeKindSchema,
  id: z.string().nullable(),
  code: z.string(),
  name: z.string(),
});
export type ScopeRef = z.infer<typeof ScopeRefSchema>;

export const AccessSourceKindSchema = z.enum(['position', 'cover', 'grant']);
export type AccessSourceKind = z.infer<typeof AccessSourceKindSchema>;

/** Where a permission came from: a seat held, a seat covered, or an explicit grant. */
export const AccessSourceSchema = z.object({
  kind: AccessSourceKindSchema,
  /** The seat (position and cover sources). */
  positionCode: z.string().nullable(),
  positionTitle: z.string().nullable(),
  /** Substantive holder vs acting holder of the seat (position sources only). */
  isActing: z.boolean(),
  roleCode: z.string(),
  roleName: z.string(),
  scope: ScopeRefSchema,
  /** Who set up the grant or cover; null for seats and imported rows. */
  grantedBy: z.object({ id: z.string(), name: z.string() }).nullable(),
  /** YYYY-MM-DD; `to` is null when open-ended. */
  from: z.iso.date().nullable(),
  to: z.iso.date().nullable(),
});
export type AccessSource = z.infer<typeof AccessSourceSchema>;

/** One permission the person holds, with the broadest scope it applies at and every source. */
export const PermissionAccessSchema = z.object({
  code: z.string(),
  /** The broadest scope among the sources (company > division > region > site). */
  scope: ScopeRefSchema,
  /** Every maximal scope: more than one when the person covers two unrelated sites. */
  scopes: z.array(ScopeRefSchema),
  /** The sites those scopes resolve to (every site for company scope). */
  siteCodes: z.array(z.string()),
  sources: z.array(AccessSourceSchema),
});
export type PermissionAccess = z.infer<typeof PermissionAccessSchema>;

/** A choice in the shell's scope switcher. */
export const AccessScopeSchema = ScopeRefSchema.extend({
  /** How the switcher labels it: the scope name, or "Acting for <seat title>" for a cover. */
  label: z.string(),
  /** Where this scope came from: a seat, a cover, a grant or the person's home site. */
  source: z.enum(['position', 'cover', 'grant', 'home']),
  /**
   * Region and division scopes: the sites inside, by name, so the switcher can offer one branch
   * under its region (handoff: branches indented under the region). Absent for sites and company.
   */
  sites: z.array(ScopeRefSchema).optional(),
  /**
   * Company and division scopes: the regions inside, each with its sites, so someone with the
   * whole company can still narrow to one region or one branch. Absent for regions and sites.
   */
  regions: z.array(ScopeRefSchema.extend({ sites: z.array(ScopeRefSchema) })).optional(),
});
export type AccessScope = z.infer<typeof AccessScopeSchema>;

export const EffectiveAccessSchema = z.object({
  permissions: z.array(PermissionAccessSchema),
  scopes: z.array(AccessScopeSchema),
});
export type EffectiveAccess = z.infer<typeof EffectiveAccessSchema>;

// Sections -------------------------------------------------------------------

/** The eight Aroo sections, in nav order. The web's nav metadata (icons, paths) keys off these. */
export const SectionIdSchema = z.enum([
  'sales',
  'operations',
  'people',
  'safety',
  'customers',
  'hr',
  'property',
  'fleet',
]);
export type SectionId = z.infer<typeof SectionIdSchema>;

export const SECTION_LABELS: Record<SectionId, string> = {
  sales: 'Sales',
  operations: 'Operations',
  people: 'People',
  safety: 'Safety',
  customers: 'Customers',
  hr: 'HR',
  property: 'Property',
  fleet: 'Fleet',
};

/**
 * Permission areas (the first segment of `area.screen.action`) that belong to each section.
 * A section is visible when the person holds any permission in one of its areas; the API
 * still checks every request (nothing is hidden only in the UI).
 */
export const SECTION_AREAS: Record<SectionId, readonly string[]> = {
  sales: ['sales'],
  operations: ['operations'],
  people: ['people'],
  safety: ['safety'],
  customers: ['customers'],
  hr: ['hr', 'holidays', 'absence', 'attendance'],
  property: ['property'],
  fleet: ['fleet'],
};

export function sectionForPermission(code: string): SectionId | null {
  const area = code.split('.')[0] ?? '';
  for (const id of SectionIdSchema.options) {
    if (SECTION_AREAS[id].includes(area)) return id;
  }
  return null;
}

// Person › Access & why ------------------------------------------------------

/** VIEW < EDIT < APPROVE < MANAGE, from the actions the person holds in the section. */
export const AccessLevelSchema = z.enum(['VIEW', 'EDIT', 'APPROVE', 'MANAGE']);
export type AccessLevel = z.infer<typeof AccessLevelSchema>;

export const AccessReasonSchema = z.object({
  kind: AccessSourceKindSchema,
  /** Human-readable, e.g. "Branch Manager, Stafford (position since 3 Mar 2026)". */
  text: z.string(),
});
export type AccessReason = z.infer<typeof AccessReasonSchema>;

export const SectionAccessSchema = z.object({
  id: SectionIdSchema,
  label: z.string(),
  scope: ScopeRefSchema,
  /** "Stafford", "North & Midlands", "Whole company", "Stafford, Stone + 2 more". */
  scopeLabel: z.string(),
  level: AccessLevelSchema,
  /** True when none of it comes from a held seat: it is all grants and covers. */
  extra: z.boolean(),
  reasons: z.array(AccessReasonSchema),
  permissions: z.array(z.string()),
});
export type SectionAccess = z.infer<typeof SectionAccessSchema>;

export const PersonAccessSchema = z.object({
  personId: z.uuid(),
  name: z.string(),
  firstName: z.string(),
  sections: z.array(SectionAccessSchema),
  cannotSee: z.array(z.object({ id: SectionIdSchema, label: z.string() })),
  /** Whether the caller may "View as" this person (people.access.view_as at a covering scope). */
  canViewAs: z.boolean(),
  /** Scope choices this person would see in the switcher. */
  scopes: z.array(AccessScopeSchema),
  computedAt: z.iso.datetime(),
});
export type PersonAccess = z.infer<typeof PersonAccessSchema>;

// View as ---------------------------------------------------------------------

export const ViewingAsSchema = z.object({
  personId: z.uuid(),
  name: z.string(),
  initials: z.string(),
});
export type ViewingAs = z.infer<typeof ViewingAsSchema>;

/** 403 bodies from the route guards and the view-as read-only rule. */
export const ForbiddenErrorSchema = z.object({
  error: z.enum(['forbidden', 'read_only_view_as']),
  message: z.string(),
  permission: z.string().nullable().optional(),
  scope: z.object({ kind: AccessScopeKindSchema, id: z.string().nullable() }).nullable().optional(),
});
export type ForbiddenError = z.infer<typeof ForbiddenErrorSchema>;
